import { Loading } from '@arcadiapower/shrike';
import { UserMachine } from '~/machines/user-creation';
import { UtilityMachine } from '~/machines/utility-selection';
import { MachineScreenStates, ScreenMap } from '~/machines/utils/types';
import { BillingSelection } from '~/screens/billing-selection';
import { BillingContainer } from '~/screens/billing/billing.container';
import { CreateAccountGoogleContainer } from '~/screens/create-account-google/create-account-google.container';
import { CreateAccountContainer } from '~/screens/create-account/create-account.container';
import { CredentialsContainer } from '~/screens/credentials/credentials.container';
import { CsUnavailableContainer } from '~/screens/cs-unavailable/cs-unavailable.container';
import { Friction, Unexpected } from '~/screens/errors';
import { PaymentHoldContainer } from '~/screens/payment-hold/payment-hold.container';
import { SelectUtilityContainer } from '~/screens/select-utility/select-utility.container';
import { BillingMachine } from '../billing-selection';
import { EnrollmentMachine } from '../enrollment-selection';
import { MembershipFunnelMachine } from './types';

export type MembershipScreenMap = {
  utility: ScreenMap<MembershipFunnelMachine, UtilityMachine>;
  user: ScreenMap<MembershipFunnelMachine, UserMachine>;
  enrollment: ScreenMap<MembershipFunnelMachine, EnrollmentMachine>;
  billing: ScreenMap<MembershipFunnelMachine, BillingMachine>;
  fetchPromoData: ScreenMap<MembershipFunnelMachine>;
  fetchEnrollmentOptions: ScreenMap<MembershipFunnelMachine>;
  billingExperience: ScreenMap<MembershipFunnelMachine>;
  completed: ScreenMap<MembershipFunnelMachine>;
  unexpected: ScreenMap<MembershipFunnelMachine>;
  csUnavailable: ScreenMap<MembershipFunnelMachine>;
};

export const membershipScreenMap: Readonly<MembershipScreenMap> = {
  utility: {
    utility: SelectUtilityContainer,
    invalidProjectId: () => <CsUnavailableContainer />,
    bouncer: () => <Loading />,
    friction: () => <Friction />,
    redirect: () => <Loading />,
    completed: () => <Loading />,
  },
  user: {
    microLearning: () => <Loading />,
    user: CreateAccountContainer,
    google: CreateAccountGoogleContainer,
    completed: () => <Loading />,
  },
  enrollment: {
    utilityConnect: CredentialsContainer,
    accountNumber: () => (
      <Unexpected reason="Account number enrollment not allowed for membership" />
    ),
    billUpload: () => <Unexpected reason="Bill upload enrollment not allowed for membership" />,
    fallBackAccountNumber: () => (
      <Unexpected reason="Account number enrollment not allowed for membership" />
    ),
    ameren: () => <Unexpected reason="Ameren is not a membership utility" />,
    amerenAuthTimeout: () => <Loading />,
    nyLmiZoneOptions: () => <Loading />,
    isInLmiZone: () => <Loading />,
    completed: () => <Loading />,
  },
  billingExperience: ({ funnel: { context, send }, overrideCopy }) => (
    <BillingSelection
      context={{
        options: ['acb', 'ucb'],
        selection: context.billingSelection,
        utility: context.funnelPacket!.utility,
      }}
      next={data =>
        send({ type: 'SELECTED_BILLING_OPTION', data: { billingSelection: data.selection } })
      }
      overrideCopy={overrideCopy}
    />
  ),
  billing: {
    fetching: () => <Loading />,
    payment: ({ funnel, micro, overrideCopy }) => (
      <BillingContainer
        micro={micro}
        funnel={funnel}
        overrideCopy={overrideCopy}
        overrideProps={{
          context: {
            // @ts-expect-error - using in overrideCopy
            sponsored: funnel.context.sponsored,
            sponsoredBy: funnel.context.sponsoredBy,
          },
        }}
      />
    ),
    hold: PaymentHoldContainer,
    completed: () => <Loading />,
  },
  fetchPromoData: () => <Loading />,
  fetchEnrollmentOptions: () => <Loading />,
  completed: () => <Loading />,
  unexpected: () => <Unexpected />,
  csUnavailable: () => <CsUnavailableContainer />,
};

// NB: do not consume - if this errors, a state is missing a corresponding screen in the map
export const _completenessCheck: Record<
  MachineScreenStates<MembershipFunnelMachine>,
  any
> = membershipScreenMap;
